import React from "react";

import { dateTimeStr } from "../../../libs/Util";
import AcumulateChart from "../../../molecules/AcumulateChart";
import RadioField from "../../../molecules/RadioField";
import ReportEntry from "../../../organisms/manager/ReportEntry";
import { ModalContext } from "../../../libs/ModalProvider";
import Confirm from "../../../molecules/Confirm";
import stock_c_to_c_example from "../../../assets/images/stock_c_to_c_example.png";
import {
  ISummaryPerClass,
  ISummaryPerTeacher,
  ITotalStatus,
  IReportEntry,
} from "../../../state";

import styles from "./StatusUtilizationBoard.module.scss";

export interface IStatusUtilizationBoardDispatchProps {
  fetchTotalStatus: (target: string) => void;
  fetchReports: (target: string) => void;
  fetchSummaryPerClass: (target: string, span: string) => void;
  fetchSummaryPerTeacher: (target: string, span: string) => void;
  deleteRealization: (id: number) => void;
}

export interface IStatusUtilizationBoardStateProps {
  total_status: ITotalStatus;
  report_entries: IReportEntry[];
  class_summaries: ISummaryPerClass[];
  teacher_summaries: ISummaryPerTeacher[];
}

export interface IStatusUtilizationBoardProps
  extends IStatusUtilizationBoardDispatchProps,
    IStatusUtilizationBoardStateProps {
  title: string;
  updateSchoolStockCToC: (schoolStockCToC: boolean) => void;
  schoolStockCToCEnabled: boolean;
}

export const StatusUtilizationBoard = (props: IStatusUtilizationBoardProps) => {
  const ctx = React.useContext(ModalContext);
  const target = localStorage.getItem("status_utilization_target") || "1";
  const [state, setState] = React.useState({
    target,
    classSpan: "week",
    teacherSpan: "week",
  });
  const fetchTotalStatus = props.fetchTotalStatus;
  const fetchReports = props.fetchReports;
  const fetchSummaryPerClass = props.fetchSummaryPerClass;
  const fetchSummaryPerTeacher = props.fetchSummaryPerTeacher;

  const handleDelete = React.useCallback(
    (realization_id: number) => {
      ctx.actions.showModal(
        <Confirm
          content={
            <>
              <div>ストックを削除しますか？</div>
              <div>この操作は取り消しができません。</div>
            </>
          }
          variant="danger"
          confirmText="削除"
          callback={() => props.deleteRealization(realization_id)}
          closeModal={ctx.actions.closeModal}
        />,
      );
    },
    [ctx.actions, props],
  );

  React.useEffect(() => {
    localStorage.setItem("status_utilization_target", state.target);
    fetchTotalStatus(state.target);
  }, [state.target, fetchTotalStatus]);

  React.useEffect(() => {
    fetchReports(state.target);
  }, [state.target, fetchReports]);

  React.useEffect(() => {
    fetchSummaryPerClass(state.target, state.classSpan);
  }, [state.target, state.classSpan, fetchSummaryPerClass]);

  React.useEffect(() => {
    fetchSummaryPerTeacher(state.target, state.teacherSpan);
  }, [state.target, state.teacherSpan, fetchSummaryPerTeacher]);

  const isSetting = state.target === "setting";
  const handleButtonClick = (value: boolean) => {
    props.updateSchoolStockCToC(value);
  };

  return (
    <div style={{ width: "100%" }}>
      <h3 className={styles.title}>{props.title}</h3>
      <div className={styles.targetSelector}>
        <div
          onClick={e => setState({ ...state, target: "1" })}
          className={state.target === "1" ? styles.activeTarget : ""}
        >
          1年生
        </div>
        <div
          onClick={e => setState({ ...state, target: "2" })}
          className={state.target === "2" ? styles.activeTarget : ""}
        >
          2年生
        </div>
        <div
          onClick={e => setState({ ...state, target: "3" })}
          className={state.target === "3" ? styles.activeTarget : ""}
        >
          3年生
        </div>
        <div
          onClick={e => setState({ ...state, target: "all" })}
          className={state.target === "all" ? styles.activeTarget : ""}
        >
          ALL
        </div>
        <div
          onClick={e => setState({ ...state, target: "setting" })}
          className={state.target === "setting" ? styles.activeTarget : ""}
        >
          設定
        </div>
      </div>
      {!isSetting ? (
        <>
          <div className={styles.box}>
            <div className={styles.subTitle}>すべての利用状況</div>
            <div className={styles.situationArea}>
              <div className={styles.item}>
                <div className={styles.itemTitle}>投稿総数</div>
                <div className={styles.itemValue}>
                  {props.total_status.realization_count}
                </div>
              </div>
              <div className={styles.item}>
                <div className={styles.itemTitle}>先生のアクション総数</div>
                <div className={styles.itemValue}>
                  {props.total_status.teachers_comment_count}
                </div>
              </div>
              <div className={styles.item}>
                <div className={styles.itemTitle}>最終投稿日</div>
                <div className={styles.itemValue}>
                  {dateTimeStr(props.total_status.last_posted_at)}
                </div>
              </div>
            </div>
          </div>
          <div className={styles.flexArea}>
            <div className={styles.box + " " + styles.graph}>
              <div className={styles.subTitle}>クラスの利用状況</div>
              <div className={styles.field}>
                <div className={styles.fieldArea}>
                  <RadioField
                    value="week"
                    master={{
                      week: "過去7日",
                      month: "過去30日",
                      year: "今年度",
                    }}
                    onChange={span => setState({ ...state, classSpan: span })}
                  />
                  <div className={styles.analyzeClass}>
                    <span className={styles.realizationExplain}>
                      ■ROOTSの数
                    </span>
                    <span className={styles.convertedRealizationExplain}>
                      ■WILLの数
                    </span>
                    <span className={styles.executedRealizationExplain}>
                      ■EXPの数
                    </span>
                  </div>
                </div>
                <AcumulateChart
                  options={{
                    width: window.innerWidth * 0.51,
                    lastAxisWidth: 100,
                  }}
                  dataSet={props.class_summaries.map(r => ({
                    name: r.class_name,
                    summaries: [
                      { color: "#3AB176", value: r.realization_count },
                      {
                        color: "#39afb1",
                        value: r.converted_realization_count,
                      },
                      { color: "#3670ae", value: r.executed_realization_count },
                    ],
                  }))}
                />
              </div>
            </div>
            <div className={styles.box + " " + styles.alert}>
              <div className={styles.subTitle}>報告されたストック</div>
              {props.report_entries.map(e => {
                return (
                  <ReportEntry
                    report_entry={e}
                    key={e.id}
                    onDelete={handleDelete}
                  />
                );
              })}
            </div>
            <div
              className={
                styles.box + " " + styles.graph + " " + styles.nextParagraph
              }
            >
              <div className={styles.subTitle}>先生のアクション</div>
              <div className={styles.field}>
                <div className={styles.fieldArea}>
                  <RadioField
                    value="week"
                    master={{
                      week: "過去7日",
                      month: "過去30日",
                      year: "今年度",
                    }}
                    onChange={span => setState({ ...state, teacherSpan: span })}
                  />
                  <div className={styles.analyzeClass}>
                    <span className={styles.feedbackExplain}>
                      ■個人への返信の数
                    </span>
                    <span className={styles.commentExplain}>
                      ■みんなへの返信の数
                    </span>
                  </div>
                </div>
                <AcumulateChart
                  options={{
                    width: window.innerWidth * 0.41,
                    nameWidth: 200,
                  }}
                  dataSet={props.teacher_summaries.map(r => ({
                    name: r.teacher_name,
                    summaries: [
                      { color: "#3670ae", value: r.feedback_count },
                      { color: "#39afb1", value: r.comment_count },
                    ],
                  }))}
                />
              </div>
            </div>
          </div>
        </>
      ) : (
        <>
          <div className={styles.box}>
            <div className={styles.settingArea}>
              <div className={styles.subTitle}>
                他校のストックを生徒のタイムラインに表示させる
                <span className={styles.supplement}>
                  ※自校のストックも匿名で他校に公開されます
                </span>
              </div>

              <div className={styles.buttonArea}>
                <button
                  className={`${styles.settingButton} ${
                    props.schoolStockCToCEnabled ? styles.selected : ""
                  }`}
                  onClick={() => handleButtonClick(true)}
                >
                  ON
                </button>
                <button
                  className={`${styles.settingButton} ${
                    !props.schoolStockCToCEnabled ? styles.selected : ""
                  }`}
                  onClick={() => handleButtonClick(false)}
                >
                  OFF
                </button>
              </div>
            </div>
          </div>
          <div className={styles.settingDescriptionArea}>
            <h3>ストック全国共有機能</h3>
            <div className={styles.descriptionArea}>
              <span>
                ストック全国共有機能は、生徒が自校にとどまらず日本中の中高生の多種多様な考えに触れるための機能です。
              </span>
              <span>
                また、これまでは自分のためだけにストックしていた内容が、
                日本のどこかにいる中高生の成長のきっかけとなるかもしれません。
              </span>
              <span>
                それに伴い、今後のストックは、ENAGEEDを導入している教育機関に共有される仕様となります。
              </span>
            </div>
            <ul>
              <li>
                個人情報について：ストックは全て匿名です。（コメント不可、いいね・リストックは可能）
              </li>
              <li>
                共有の対象外となるストックについて：非公開ストックと引用や画像付きのストックは公開の対象外です。
              </li>
              <li>
                共有機能のON/OFFについて：このページから、いつでもON/OFFを切り替えることができます。
              </li>
            </ul>

            <img
              src={stock_c_to_c_example}
              alt="stock_c_to_c_example"
              width={700}
            />
          </div>
        </>
      )}
    </div>
  );
};
export default StatusUtilizationBoard;
